"use client";

import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
} from "firebase/firestore";
import { Lock, ShieldOff, ArrowLeft, Wallet } from "lucide-react";
import toast, { Toaster } from "react-hot-toast";
import { GridLoader } from "react-spinners";

import { db } from "../../firebase";
import { createInvoice } from "../services/authapi";

const PRESET_AMOUNTS = [5, 10, 20, 50, 100, 200];
const MIN_AMOUNT = 5;
const MAX_AMOUNT = 10000;
// The simplified CashApp-only page always routes to the existing CashApp
// Lightning BTC flow on the invoice page — that's the whole point of this
// page (business users wanted a direct CashApp link without seeing other
// payment methods).
const PAYMENT_METHOD = "lightning-cashapp";

interface UserData {
  wallet?: string;
  smartWallet?: string;
  username?: string;
  photoURL?: string;
  email?: string;
  collectSettings?: {
    requestPaymentEnabled?: boolean;
  };
  disabledMethodsByType?: Record<string, string[]>;
}

export default function CashappPayPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  const [userData, setUserData] = useState<UserData | null>(null);
  const [resolvedWallet, setResolvedWallet] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [selectedPreset, setSelectedPreset] = useState<number | null>(null);
  const [customAmount, setCustomAmount] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const fetchUser = async () => {
      setLoading(true);
      setError(null);

      try {
        if (!slug) {
          setError("Invalid payment link.");
          setLoading(false);
          return;
        }

        const slugDoc = await getDoc(doc(db, "slugs", slug.toLowerCase()));
        if (!slugDoc.exists()) {
          setError("This payment link does not exist.");
          setLoading(false);
          return;
        }
        const walletAddress: string =
          slugDoc.data().walletAddress?.toLowerCase() || "";

        const userDoc = await getDoc(doc(db, "users", walletAddress));
        if (userDoc.exists()) {
          const data = userDoc.data() as UserData;
          setUserData(data);
          setResolvedWallet(data.smartWallet || data.wallet || walletAddress);
        } else {
          const q = query(
            collection(db, "users"),
            where("smartWallet", "==", walletAddress),
          );
          const snap = await getDocs(q);
          if (snap.empty) {
            setError("User not found. This payment link may be invalid.");
            setLoading(false);
            return;
          }
          const data = snap.docs[0].data() as UserData;
          setUserData(data);
          setResolvedWallet(data.smartWallet || data.wallet || walletAddress);
        }
      } catch (err) {
        console.error("Error fetching user:", err);
        setError("Failed to load payment page. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchUser();
  }, [slug]);

  const parsedAmount = useMemo(() => {
    if (selectedPreset !== null) return selectedPreset;
    const n = parseFloat(customAmount);
    return Number.isFinite(n) && n > 0 ? n : 0;
  }, [selectedPreset, customAmount]);

  const isValidEmail = useMemo(
    () => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()),
    [email],
  );

  const amountError =
    parsedAmount > 0 && parsedAmount < MIN_AMOUNT
      ? `Minimum amount is $${MIN_AMOUNT}`
      : parsedAmount > MAX_AMOUNT
        ? `Maximum amount is $${MAX_AMOUNT.toLocaleString()}`
        : null;

  const canSubmit = parsedAmount >= MIN_AMOUNT && parsedAmount <= MAX_AMOUNT && isValidEmail && !submitting;

  const handlePresetClick = (value: number) => {
    setSelectedPreset(value);
    setCustomAmount("");
  };

  const handleCustomChange = (raw: string) => {
    setSelectedPreset(null);
    const sanitized = raw.replace(/[^0-9.]/g, "");
    const parts = sanitized.split(".");
    const clean =
      parts.length > 2 ? `${parts[0]}.${parts.slice(1).join("")}` : sanitized;
    setCustomAmount(clean);
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;

    if (!resolvedWallet) {
      toast.error("Recipient wallet missing.");
      return;
    }

    try {
      setSubmitting(true);
      const inv = await createInvoice({
        buyerEmail: email.trim(),
        amount: parsedAmount,
        sellerWallet: resolvedWallet,
        sellerUsername: userData?.username,
        source: "request",
        paymentMethod: PAYMENT_METHOD,
        collectSlug: slug,
      });
      navigate(`/payment-invoice/${inv.invoiceId}?method=${encodeURIComponent(PAYMENT_METHOD)}`, {
        state: {
          method: PAYMENT_METHOD,
          amount: parsedAmount,
          dedicatedAddress: inv.dedicatedWalletAddress,
          buyerEmail: email.trim(),
          sellerName: userData?.username,
          sellerPhoto: userData?.photoURL,
          source: "request",
          sellerWallet: resolvedWallet,
        },
      });
    } catch (err: any) {
      console.error("Failed to create invoice:", err);
      toast.error(err?.message || "Failed to create invoice. Please try again.");
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <GridLoader color="#00D632" size={14} />
      </div>
    );
  }

  if (error || !userData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white border border-gray-200 rounded-3xl p-8 max-w-md w-full text-center shadow-sm">
          <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-red-50 flex items-center justify-center">
            <Wallet className="w-8 h-8 text-red-500" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-3">Page Not Found</h2>
          <p className="text-gray-500 mb-6">
            {error || "This payment link is invalid."}
          </p>
          <Link
            to="/login"
            className="inline-flex items-center gap-2 text-[#00D632] hover:text-[#00B82A] font-medium transition-colors"
          >
            <ArrowLeft size={18} />
            Go to CashTap
          </Link>
        </div>
      </div>
    );
  }

  if (userData.collectSettings?.requestPaymentEnabled === false) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white border border-gray-200 rounded-3xl p-8 max-w-md w-full text-center shadow-sm">
          <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-orange-50 flex items-center justify-center">
            <ShieldOff className="w-8 h-8 text-orange-500" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-3">
            Payments Not Available
          </h2>
          <p className="text-gray-500 mb-6">
            This user has disabled payments. Please contact them directly.
          </p>
          <Link
            to="/login"
            className="inline-flex items-center gap-2 text-[#00D632] hover:text-[#00B82A] font-medium transition-colors"
          >
            <ArrowLeft size={18} />
            Go to CashTap
          </Link>
        </div>
      </div>
    );
  }

  const username = userData.username || "CashTap User";
  const initial = username.charAt(0).toUpperCase();
  const displayAmount = parsedAmount.toFixed(2);
  const [dollars, cents] = displayAmount.split(".");

  return (
    <div className="min-h-screen flex justify-center items-center bg-gradient-to-br from-gray-50 via-white to-emerald-50/40 flex-col p-4 sm:p-6 [@media(max-height:780px)]:p-3">
      <Toaster position="top-center" />

      <div className="relative w-full max-w-md">
        <div
          className="absolute -inset-px rounded-[26px] bg-gradient-to-br from-[#00D632]/40 via-[#00D632]/10 to-transparent blur-md"
          aria-hidden="true"
        />
        <div
          className="absolute inset-x-6 -bottom-4 h-8 rounded-full bg-[#00D632]/25 blur-2xl"
          aria-hidden="true"
        />
        <div className="relative bg-white rounded-[24px] border border-[#00D632]/20 ring-1 ring-[#00D632]/10 shadow-[0_20px_55px_-18px_rgba(0,214,50,0.35),0_8px_24px_-12px_rgba(0,0,0,0.08)] px-6 sm:px-8 py-8 sm:py-10 [@media(max-height:780px)]:h-[90vh] [@media(max-height:780px)]:flex [@media(max-height:780px)]:flex-col [@media(max-height:780px)]:justify-center [@media(max-height:780px)]:px-5 [@media(max-height:780px)]:py-6">
        <div className="flex flex-col items-center">
          <div className="relative">
            <div className="absolute -inset-1.5 rounded-[26px] bg-gradient-to-br from-[#00D632]/40 via-[#00D632]/20 to-transparent blur-md" aria-hidden="true" />
            <div className="relative w-20 h-20 rounded-2xl bg-gradient-to-br from-[#00E840] via-[#00D632] to-[#00B82A] flex items-center justify-center overflow-hidden ring-2 ring-white shadow-[0_8px_24px_-6px_rgba(0,214,50,0.55),inset_0_1px_0_0_rgba(255,255,255,0.25)] [@media(max-height:780px)]:w-16 [@media(max-height:780px)]:h-16 [@media(max-height:780px)]:rounded-xl">
              {userData.photoURL ? (
                <img
                  src={userData.photoURL}
                  alt={username}
                  className="w-full h-full object-cover"
                />
              ) : (
                <span className="text-white text-3xl font-bold drop-shadow-sm [@media(max-height:780px)]:text-3xl">{initial}</span>
              )}
            </div>
            <div className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-white flex items-center justify-center shadow-md [@media(max-height:780px)]:w-5 [@media(max-height:780px)]:h-5">
              <div className="w-4 h-4 rounded-full bg-[#00D632] flex items-center justify-center [@media(max-height:780px)]:w-3.5 [@media(max-height:780px)]:h-3.5">
                <svg viewBox="0 0 24 24" fill="none" className="w-3 h-3 text-white [@media(max-height:780px)]:w-2.5 [@media(max-height:780px)]:h-2.5" aria-hidden="true">
                  <path d="M5 12l4.5 4.5L19 7" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
            </div>
          </div>

          <h1 className="mt-5 text-2xl font-bold text-gray-900 tracking-tight [@media(max-height:780px)]:mt-4 [@media(max-height:780px)]:text-xl">{username}</h1>
          <div className="mt-1.5 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 text-xs font-medium [@media(max-height:780px)]:mt-1 [@media(max-height:780px)]:text-[10px] [@media(max-height:780px)]:gap-1 [@media(max-height:780px)]:px-2 [@media(max-height:780px)]:py-0.5">
            <Lock size={12} strokeWidth={2.5} className="[@media(max-height:780px)]:w-2.5 [@media(max-height:780px)]:h-2.5" />
            <span>Secure Payment</span>
          </div>
        </div>

        <div className="mt-8 flex items-start justify-center font-bold text-gray-900 [@media(max-height:780px)]:mt-5">
          <span className="text-3xl sm:text-4xl mt-2 [@media(max-height:780px)]:text-2xl [@media(max-height:780px)]:mt-1.5">$</span>
          <span className="text-6xl sm:text-7xl leading-none tracking-tight [@media(max-height:780px)]:text-5xl">
            {dollars}
          </span>
          <span className="text-3xl sm:text-4xl mt-2 [@media(max-height:780px)]:text-2xl [@media(max-height:780px)]:mt-1.5">.{cents}</span>
        </div>

        <div className="mt-8 grid grid-cols-3 gap-3 [@media(max-height:780px)]:mt-5 [@media(max-height:780px)]:gap-2.5">
          {PRESET_AMOUNTS.map((value) => {
            const active = selectedPreset === value;
            return (
              <button
                key={value}
                type="button"
                onClick={() => handlePresetClick(value)}
                className={`h-11 rounded-full text-sm font-semibold transition-colors [@media(max-height:780px)]:h-9 [@media(max-height:780px)]:text-sm ${
                  active
                    ? "bg-[#00D632] text-white"
                    : "bg-gray-100 text-gray-900 hover:bg-gray-200"
                }`}
              >
                ${value}
              </button>
            );
          })}
        </div>

        <div className="mt-6 [@media(max-height:780px)]:mt-4">
          <label
            htmlFor="custom-amount"
            className="block text-xs font-semibold tracking-wide text-gray-500 uppercase mb-2 [@media(max-height:780px)]:text-[11px] [@media(max-height:780px)]:mb-1.5"
          >
            Custom Amount
          </label>
          <div className="relative">
            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 font-medium [@media(max-height:780px)]:left-3.5 [@media(max-height:780px)]:text-sm">
              $
            </span>
            <input
              id="custom-amount"
              type="text"
              inputMode="decimal"
              value={customAmount}
              onChange={(e) => handleCustomChange(e.target.value)}
              placeholder="0.00"
              className={`w-full h-12 pl-9 pr-4 rounded-2xl border bg-white text-gray-900 placeholder-gray-300 focus:outline-none focus:ring-2 transition-all [@media(max-height:780px)]:h-10 [@media(max-height:780px)]:text-sm [@media(max-height:780px)]:pl-8 [@media(max-height:780px)]:rounded-xl ${
                amountError
                  ? "border-red-400 focus:border-red-400 focus:ring-red-400/20"
                  : "border-gray-200 focus:border-[#00D632] focus:ring-[#00D632]/20"
              }`}
            />
          </div>
          {amountError && (
            <p className="mt-1.5 text-xs text-red-500">{amountError}</p>
          )}
          {!amountError && (
            <p className="mt-1.5 text-xs font-semibold text-[#00D632]">Min $5 · Max $10,000</p>
          )}
        </div>

        <div className="mt-4 [@media(max-height:780px)]:mt-3">
          <label
            htmlFor="payer-email"
            className="block text-xs font-semibold tracking-wide text-gray-500 uppercase mb-2 [@media(max-height:780px)]:text-[11px] [@media(max-height:780px)]:mb-1.5"
          >
            Email
          </label>
          <input
            id="payer-email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            className="w-full h-12 px-4 rounded-2xl border border-gray-200 bg-white text-gray-900 placeholder-gray-300 focus:outline-none focus:border-[#00D632] focus:ring-2 focus:ring-[#00D632]/20 transition-all [@media(max-height:780px)]:h-10 [@media(max-height:780px)]:text-sm [@media(max-height:780px)]:rounded-xl"
          />
        </div>

        <button
          type="button"
          onClick={handleSubmit}
          disabled={!canSubmit}
          className={`mt-7 w-full h-12 rounded-full text-base font-semibold transition-colors [@media(max-height:780px)]:mt-5 [@media(max-height:780px)]:h-11 [@media(max-height:780px)]:text-sm ${
            canSubmit
              ? "bg-[#00D632] text-white hover:bg-[#00B82A] shadow-sm"
              : "bg-gray-200 text-gray-400 cursor-not-allowed"
          }`}
        >
          {submitting ? "Creating invoice…" : "Pay"}
        </button>

        <div className="mt-6 [@media(max-height:780px)]:mt-5">
          <div className="relative flex items-center">
            <div className="flex-1 h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent" />
            <div className="inline-flex items-center gap-1.5 mx-3 px-3 py-1 rounded-full bg-white border border-gray-100 shadow-sm [@media(max-height:780px)]:px-2.5 [@media(max-height:780px)]:py-0.5">
              <span className="text-[9px] font-semibold tracking-[0.14em] text-gray-400 uppercase [@media(max-height:780px)]:text-[8px]">
                Powered by
              </span>
              <span className="flex items-center gap-1">
                <span className="relative flex h-1.5 w-1.5">
                  <span className="absolute inline-flex h-full w-full rounded-full bg-[#00D632] opacity-60 animate-ping" />
                  <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-[#00D632]" />
                </span>
                <span className="text-xs font-bold tracking-tight bg-gradient-to-r from-[#00B82A] to-[#00D632] bg-clip-text text-transparent [@media(max-height:780px)]:text-[11px]">
                  CashTap
                </span>
              </span>
            </div>
            <div className="flex-1 h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent" />
          </div>
        </div>
      </div>
      </div>
    </div>
  );
}
